import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import React, { forwardRef, MouseEvent, useState } from 'react';

import OberonCard from '~components/OberonCard';
import OberonDialog from '~components/OberonDialog';
import { PublicHolidays } from '~hooks/usePublicHoliday/domain';

interface PublicHolidayListCardProps {
  publicHolidayList: PublicHolidays;
  onEdit?: () => void;
  onDelete?: () => void;
}

const formatDate = (year: number, month: number, day: number): string => {
  const dd = String(day).padStart(2, '0');
  const mm = String(month).padStart(2, '0');
  return `${dd}/${mm}/${year}`;
};

const getDayOfWeek = (year: number, month: number, day: number): string => {
  return new Date(year, month - 1, day).toLocaleDateString('en-AU', { weekday: 'long' });
};

const formatPostcode = (postcodes: string[]): string => {
  return postcodes.map((pc) => pc.trim()).join(', ');
};

const PublicHolidayListCard = forwardRef<HTMLDivElement, PublicHolidayListCardProps>(
  ({ publicHolidayList, onEdit, onDelete }, ref) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState<boolean>(false);
    const publicHolidayId = formatDate(publicHolidayList.year, publicHolidayList.month, publicHolidayList.day);
    const validPostcodes = publicHolidayList.postcodes?.filter((pc) => pc.trim()) ?? [];

    const handleMenuOpen = (e: MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(e.currentTarget);
    };
    const handleMenuClose = () => {
      setAnchorEl(null);
    };

    const triggerEdit = () => {
      setAnchorEl(null);
      onEdit?.();
    };

    const triggerDelete = () => {
      onDelete?.();
      setDeleteConfirmModalOpen(false);
    };

    return (
      <>
        <OberonCard
          ref={ref}
          titleFontWeight={700}
          title={
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              {publicHolidayList.name}
              <Box component='span' sx={{ color: 'text.secondary', fontSize: '0.9rem', fontWeight: 400 }}>
                {formatDate(publicHolidayList.year, publicHolidayList.month, publicHolidayList.day)}
                {' - '}
                {getDayOfWeek(publicHolidayList.year, publicHolidayList.month, publicHolidayList.day)}
              </Box>
            </Box>
          }
          subHeader={
            <>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', my: 0.5 }}>
                {publicHolidayList.timezones.map((tz) => (
                  <Chip key={tz} label={tz} size='small' color='default' />
                ))}
              </Box>
              {validPostcodes.length > 0 && (
                <Box sx={{ color: 'text.primary', fontSize: '1rem', mt: 0.5 }}>
                  Postcode: {formatPostcode(validPostcodes)}
                </Box>
              )}
              <Box sx={{ color: 'text.secondary', mt: 0.5 }}>{publicHolidayList.information}</Box>
            </>
          }
          action={
            <>
              <IconButton
                aria-controls={publicHolidayId ? publicHolidayId : undefined}
                aria-haspopup={publicHolidayId ? true : undefined}
                onClick={handleMenuOpen}>
                <MoreHorizIcon />
              </IconButton>

              {(onEdit || onDelete) && (
                <Menu
                  id={publicHolidayId}
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}>
                  {onEdit && <MenuItem onClick={triggerEdit}>Edit</MenuItem>}
                  {onDelete && <MenuItem onClick={() => setDeleteConfirmModalOpen(true)}>Delete</MenuItem>}
                </Menu>
              )}
            </>
          }
        />
        <OberonDialog
          open={deleteConfirmModalOpen}
          title='Are you sure you want to do this?'
          content={
            <Box>
              <Typography>
                You are about to delete the public holiday <strong>{publicHolidayList.name}</strong>, once you have
                completed this action it cannot be undone.
              </Typography>
              <Typography
                variant='caption'
                sx={{
                  display: 'block',
                  color: 'red',
                  mt: 1,
                }}>
                This option will delete the Public holiday.
              </Typography>
            </Box>
          }
          actionFooter={
            <>
              <Button variant='text' onClick={() => setDeleteConfirmModalOpen(false)}>
                Close
              </Button>
              <Button variant='contained' onClick={triggerDelete}>
                Delete
              </Button>
            </>
          }
          onClose={() => {
            handleMenuClose();
            setDeleteConfirmModalOpen(false);
          }}
          onSubmit={triggerDelete}
        />
      </>
    );
  },
);

export default PublicHolidayListCard;
